// Stores already-parsed DOM fragments keyed by URL so clicks are instant
const domCache = new Map();

document.addEventListener("DOMContentLoaded", function () {
  // Prefetch + pre-parse color variants on initial page load
  const productWrapper = document.querySelector('.product__page--wrapper');
  if (productWrapper) {
    const sectionId = productWrapper.dataset.sectionId;
    if (sectionId) {
      // Cache a clone of the initial product DOM so back navigation can
      // restore it instantly (without waiting on a network fetch).
      const initialPath = window.location.pathname;
      const initialCacheKey = `${initialPath}?section_id=${sectionId}`;
      if (!domCache.has(initialCacheKey)) {
        domCache.set(initialCacheKey, productWrapper.cloneNode(true));
      }
      // Track which product is currently displayed
      productWrapper.dataset.currentHandle = initialPath;

      prefetchNewColorVariants(productWrapper, sectionId);
    }

    // Seed initial history state so back/forward navigation between swatch-swapped
    // products has a consistent state to compare against.
    const initialHandle = window.location.pathname;
    if (!window.history.state || !window.history.state.productSwatchHandle) {
      window.history.replaceState(
        { productSwatchHandle: initialHandle },
        "",
        window.location.href
      );
    }
  }

  // Use event delegation - attach listener to document instead of individual buttons
  document.addEventListener("click", async function (e) {
    // Check if clicked element is a swatch button
    const button = e.target.closest(".product__swatch--button");

    if (button) {
      const productHandle = button.dataset.swatchProductLink;
      const buttonParent = button.closest(".product__page--wrapper");
      const sectionId = buttonParent?.dataset.sectionId;

      if (!productHandle || !buttonParent || !sectionId) return;

      await swapProductSection(productHandle, buttonParent, sectionId, {
        updateHistory: true,
      });
    }
  });
});

// Handle browser back/forward navigation between swatch-swapped products
window.addEventListener("popstate", async function (event) {
  const productWrapper = document.querySelector(".product__page--wrapper");
  if (!productWrapper) return;

  const sectionId = productWrapper.dataset.sectionId;
  if (!sectionId) return;

  const targetHandle = window.location.pathname;

  // Only act on product URLs — let the browser handle anything else natively
  if (!targetHandle.startsWith("/products/")) return;

  // Skip if we're already displaying this product (e.g. hash-only change)
  if (productWrapper.dataset.currentHandle === targetHandle) return;

  await swapProductSection(targetHandle, productWrapper, sectionId, {
    updateHistory: false,
    useCache: true,
  });
});

// Shared product-section swap used by both swatch clicks and popstate navigation.
//   updateHistory: true   → push new history entry (swatch click)
//                  false  → URL already updated by browser (popstate / back-forward)
//   useCache:      true   → read in-memory cache when available (instant back-nav on mobile)
//                  false  → cache-busted network fetch (always fresh — default for swatch clicks)
async function swapProductSection(productHandle, buttonParent, sectionId, { updateHistory = true, useCache = false } = {}) {
  // Prevent double-clicks while loading
  buttonParent.style.pointerEvents = "none";

  try {
    const fullUrl = `${productHandle}?section_id=${sectionId}`;
    const pairsWellWithSectionPromise = getOrFetchPairsWellWithSection(productHandle);

    // ── 1. Read swatch order BEFORE any DOM changes (batch reads) ──
    const swatchOrderMap = readSwatchOrder(buttonParent);

    // ── 2. Section HTML
    //   • Swatch click  → cache-busted fetch so price/inventory are always fresh
    //   • Back/forward  → in-memory cache lookup (falls through to network on miss)
    let newProductSection;
    if (useCache) {
      newProductSection = await getOrFetchSection(fullUrl);
    } else {
      const bustSep = fullUrl.includes("?") ? "&" : "?";
      newProductSection = await getOrFetchSection(
        `${fullUrl}${bustSep}_=${Date.now()}`,
        fullUrl
      );
    }

    if (!newProductSection) {
      // Couldn't fetch — fall back to full navigation so the user isn't stuck
      if (updateHistory) {
        window.location.href = productHandle;
      } else {
        window.location.reload();
      }
      return;
    }

    const currentImagesContainer = buttonParent.querySelector("product-images");
    const newProductImages = newProductSection.querySelector("product-images");

    // ── 3. Crossfade: start fade-out then immediately swap content ──
    currentImagesContainer.classList.add("fading-out");

    // Use rAF to batch all DOM writes in one frame
    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        // Swap images
        currentImagesContainer.innerHTML = newProductImages.innerHTML;
        currentImagesContainer.classList.remove("fading-out");

        // innerHTML never executes the <script> tags it contains (e.g. the gallery
        // scrollbar init in sections/product.liquid) - recreate each one so it runs
        // against this color's freshly-inserted markup instead of staying dead.
        reviveScripts(currentImagesContainer);

        // ── 4. Update selectors ──
        const newSelectorsHtml = newProductSection.querySelector(
          ".product__form__outer .product__selectors"
        );
        const currentSelectors = buttonParent.querySelector(
          ".product__form__outer .product__selectors"
        );
        if (currentSelectors && newSelectorsHtml) {
          currentSelectors.innerHTML = newSelectorsHtml.innerHTML;
        }

        // The freshly loaded product starts with nothing selected either —
        // require a fresh explicit pick before showing the availability slash again.
        delete buttonParent.dataset.variantSelectionMade;

        // ── 5. Re-apply swatch order values ──
        applySwatchOrder(buttonParent, swatchOrderMap);

        // ── 6. Update form data (keeps event listeners intact) ──
        updateProductFormData(buttonParent, newProductSection);

        // ── 6b. Update sticky cart bar (title, price, CTA, size picker) ──
        if (window.MalbonCartBar && typeof window.MalbonCartBar.updateFromSection === "function") {
          window.MalbonCartBar.updateFromSection(buttonParent, newProductSection);
        }

        // ── 7. Update URL without reload (skip on popstate — URL already changed) ──
        if (updateHistory) {
          window.history.pushState(
            { productSwatchHandle: productHandle },
            "",
            productHandle
          );
        }

        // Track what's currently displayed so popstate can short-circuit no-op events
        buttonParent.dataset.currentHandle = productHandle;

        // ── 8. Update product-specific companion section ──
        pairsWellWithSectionPromise
          .then(updatePairsWellWithSection)
          .catch((error) => {
            console.error("Error updating pairs well with section:", error);
          });

        // ── 9. Re-enable interaction ──
        buttonParent.style.opacity = "1";
        buttonParent.style.pointerEvents = "auto";

        // ── 10. Prefetch new neighbor variants in background ──
        prefetchNewColorVariants(buttonParent, sectionId);

        resolve();
      });
    });
  } catch (error) {
    console.error("Error loading product:", error);
    // Fallback to normal navigation
    if (updateHistory) {
      window.location.href = productHandle;
    } else {
      window.location.reload();
    }
  } finally {
    // Safety net — ensure interaction is always re-enabled
    buttonParent.style.opacity = "1";
    buttonParent.style.pointerEvents = "auto";
  }
}

// ── Helper: re-run <script> tags inside a container whose innerHTML was just
// replaced. Assigning .innerHTML parses the markup but never executes any
// <script> it contains - only a script element created via createElement (or
// a clone of one already running) and inserted into the document actually runs. ──
function reviveScripts(container) {
  container.querySelectorAll("script").forEach((oldScript) => {
    const newScript = document.createElement("script");
    Array.from(oldScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });
    newScript.textContent = oldScript.textContent;
    oldScript.replaceWith(newScript);
  });
}

// ── Helper: read swatch order map from current DOM (batch read) ──────
function readSwatchOrder(section) {
  const map = new Map();
  const swatches = section.querySelectorAll(
    ".product__form__outer .product__selectors radio-swatch"
  );
  swatches.forEach((swatch) => {
    const btn = swatch.querySelector(".product__swatch--button");
    if (btn?.dataset.swatchProductLink) {
      const orderMatch = (swatch.getAttribute("style") || "").match(/order:\s*(\d+)/);
      if (orderMatch) {
        map.set(btn.dataset.swatchProductLink, orderMatch[1]);
      }
    }
  });
  return map;
}

// ── Helper: apply swatch order map to updated DOM (batch write) ──────
function applySwatchOrder(section, orderMap) {
  if (orderMap.size === 0) return;
  const swatches = section.querySelectorAll(
    ".product__form__outer .product__selectors radio-swatch"
  );
  swatches.forEach((swatch) => {
    const btn = swatch.querySelector(".product__swatch--button");
    const link = btn?.dataset.swatchProductLink;
    if (link && orderMap.has(link)) {
      const orderValue = orderMap.get(link);
      const style = swatch.getAttribute("style") || "";
      swatch.setAttribute(
        "style",
        style.includes("order:")
          ? style.replace(/order:\s*\d+;?/g, `order: ${orderValue};`)
          : style + (style.trim().endsWith(";") ? "" : ";") + ` order: ${orderValue};`
      );
    }
  });
}

// ── Helper: return cached parsed section or fetch+parse+cache ────────
// Two args (fetchUrl, cacheKey): always network fetch with fetchUrl, store under domCache[cacheKey] (e.g. color swap).
// One arg: original behavior (cache hit returns clone immediately).
async function getOrFetchSection(url, cacheKey) {
  if (cacheKey !== undefined) {
    const response = await fetch(url, { priority: "high" });
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const section = doc.querySelector(".product__page--wrapper");
    if (section) {
      domCache.set(cacheKey, section);
      return section.cloneNode(true);
    }
    return null;
  }
  if (domCache.has(url)) {
    return domCache.get(url).cloneNode(true);
  }
  const response = await fetch(url, { priority: "high" });
  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  const section = doc.querySelector(".product__page--wrapper");
  if (section) {
    domCache.set(url, section);
    return section.cloneNode(true);
  }
  return null;
}

async function getOrFetchPairsWellWithSection(productUrl) {
  const currentSection = document.querySelector('[data-section-type="pairs-well-with"]');
  const sectionId = currentSection?.dataset.sectionId;
  if (!sectionId || !productUrl) return null;

  const sectionUrl = new URL(productUrl, window.location.origin);
  const cacheKey = `pairs-well-with:${sectionUrl.pathname}:${sectionId}`;

  if (domCache.has(cacheKey)) {
    return domCache.get(cacheKey).cloneNode(true);
  }

  sectionUrl.searchParams.set("section_id", sectionId);
  sectionUrl.searchParams.set("_", Date.now());

  try {
    const response = await fetch(sectionUrl.toString(), { priority: "low" });
    if (!response.ok) return null;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const section = doc.querySelector('[data-section-type="pairs-well-with"]');

    if (section) {
      domCache.set(cacheKey, section);
      return section.cloneNode(true);
    }
  } catch (error) {
    console.error("Error loading pairs well with section:", error);
  }

  return null;
}

function updatePairsWellWithSection(newSection) {
  if (!newSection) return;

  const currentSection = document.querySelector('[data-section-type="pairs-well-with"]');
  if (!currentSection) return;

  currentSection.replaceWith(newSection);

  // Let existing slider/quick-add observers bind to the freshly inserted cards.
  requestAnimationFrame(() => {
    window.dispatchEvent(new Event("wnw_load"));
    document.dispatchEvent(new CustomEvent("theme:resize:width"));
  });
}

// Update only product-specific data in the form (values, attributes, text)
// Does NOT replace elements - keeps all event listeners intact
function updateProductFormData(currentSection, newSection) {
  // 1. Update hidden variant ID input VALUE only
  const currentVariantInput = currentSection.querySelector('input[name="id"]');
  const newVariantInput = newSection.querySelector('input[name="id"]');
  if (currentVariantInput && newVariantInput) {
    currentVariantInput.value = newVariantInput.value;

    // Trigger change event so ProductForm updates
    currentVariantInput.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // 2. Update button TEXT only (not the button element)
  const currentBtnText = currentSection.querySelector(
    "[data-add-to-cart-text]"
  );
  const newBtnText = newSection.querySelector("[data-add-to-cart-text]");
  if (currentBtnText && newBtnText) {
    currentBtnText.textContent = newBtnText.textContent;
  }

  // 3. Update button DISABLED attribute only
  const currentBtn = currentSection.querySelector("[data-add-to-cart]");
  const newBtn = newSection.querySelector("[data-add-to-cart]");
  if (currentBtn && newBtn) {
    if (newBtn.hasAttribute("disabled")) {
      currentBtn.setAttribute("disabled", "disabled");
    } else {
      currentBtn.removeAttribute("disabled");
    }
  }

  // 4. Update product-specific DATA attributes (product ID, section ID, etc.)
  const currentForm = currentSection.querySelector("[data-product-form]");
  const newForm = newSection.querySelector("[data-product-form]");
  if (currentForm && newForm) {
    // Copy product-related data attributes
    Array.from(newForm.attributes).forEach((attr) => {
      if (attr.name.startsWith("data-")) {
        currentForm.setAttribute(attr.name, attr.value);
      }
    });
  }

  // 5. Update product JSON data (critical for variant switching)
  const currentJSON = currentSection.querySelector("[data-product-json]");
  const newJSON = newSection.querySelector("[data-product-json]");
  if (currentJSON && newJSON) {
    currentJSON.textContent = newJSON.textContent;
  }

  // 6. Handle preorder input (add/remove based on product tags)
  const currentPreorder = currentSection.querySelector(
    'input[name="properties[_PRE ORDER]"]'
  );
  const newPreorder = newSection.querySelector(
    'input[name="properties[_PRE ORDER]"]'
  );
  if (newPreorder && !currentPreorder) {
    // Add preorder input
    const form = currentSection.querySelector("form[data-product-form]");
    const preorderInput = document.createElement("input");
    preorderInput.type = "hidden";
    preorderInput.name = "properties[_PRE ORDER]";
    preorderInput.value = " ";
    form.insertBefore(preorderInput, form.firstChild);
  } else if (!newPreorder && currentPreorder) {
    // Remove preorder input
    currentPreorder.remove();
  }

  // 7. Show "Select Size" button and hide "Add to Cart" button on product swap
  //    but only for multi-variant products — single-variant products add directly
  const sizeUnselectedBtn = currentSection.querySelector(
    ".product-size-unselected-btn"
  );
  const addToCartBtn = currentSection.querySelector("[data-add-to-cart]");
  const outOfStockBtn = currentSection.querySelector(".klaviyo-bis-trigger");

  let isSingleVariant = false;
  const productJSON = newSection.querySelector("[data-product-json]");
  if (productJSON) {
    try {
      const productData = JSON.parse(productJSON.textContent);
      isSingleVariant = productData.variants && productData.variants.length === 1;
    } catch (e) { /* ignore parse errors */ }
  }

  if (isSingleVariant) {
    if (sizeUnselectedBtn) {
      sizeUnselectedBtn.style.display = "none";
    }
    if (addToCartBtn) {
      addToCartBtn.classList.remove("add_product_btn_hide");
    }
  } else {
    if (sizeUnselectedBtn) {
      sizeUnselectedBtn.style.display = "block";
    }
    if (addToCartBtn) {
      addToCartBtn.classList.add("add_product_btn_hide");
    }
  }

  // Hide out of stock button on product swap (will show again when size is selected)
  if (outOfStockBtn) {
    outOfStockBtn.style.display = "none";
  }

  // 8. Trigger ProductForm to reinitialize with new product data
  const productForm = currentSection.querySelector("product-form");
  if (productForm && typeof productForm.connectedCallback === "function") {
    try {
      // Re-run connectedCallback to reinitialize with new product JSON
      productForm.connectedCallback();
    } catch (e) {
      console.warn("  ⚠️ ProductForm reinit error:", e);
    }
  }
}

// Recompute color-swatch availability for the currently selected combination
// (e.g. Size). Colors are separate products (see the `prdct` loop in
// product-variant-options.liquid), not real variants of the current product,
// so their availability has to be looked up from the per-color `data-variants`
// JSON that liquid embeds on each swatch rather than from a shared variant list.
function updateColorSwatchAvailability(productSection, variant) {
  if (!variant) return;

  const productJSONEl = productSection.querySelector("[data-product-json]");
  if (!productJSONEl) return;

  let productData;
  try {
    productData = JSON.parse(productJSONEl.textContent);
  } catch (e) {
    return;
  }

  const optionNames = productData.options || [];
  const variantOptions = variant.options || [];

  // Map of option name -> selected value for the current product, excluding
  // Color (colors aren't a real option here, but skip it defensively anyway).
  const selected = {};
  optionNames.forEach((name, index) => {
    if (typeof name === "string" && name.toLowerCase() === "color") return;
    selected[name] = variantOptions[index];
  });

  const selectedKeys = Object.keys(selected);
  if (selectedKeys.length === 0) return;

  const swatches = productSection.querySelectorAll(
    ".ProductForm__Variants radio-swatch[data-variants]"
  );

  swatches.forEach((swatch) => {
    let siblingVariants;
    try {
      siblingVariants = JSON.parse(swatch.dataset.variants);
    } catch (e) {
      return;
    }

    const matchingVariant = siblingVariants.find((siblingVariant) =>
      selectedKeys.every(
        (key) => siblingVariant.options[key] === selected[key]
      )
    );

    // No matching variant for this combination on that color = treat as unavailable.
    const isAvailable = !!(matchingVariant && matchingVariant.available);

    swatch.dataset.available = isAvailable ? "true" : "false";

    const swatchImage = swatch.querySelector(".swatchImages");
    if (swatchImage) {
      swatchImage.classList.toggle("custom-swatch-slash", !isAvailable);
    }
  });
}

// theme:variant:change also fires once on initial load / after a color swap
// (the product-form custom element fires it from connectedCallback even
// though nothing is checked yet — size radios/selects start unchecked on
// purpose). We only want the availability slash to appear once the shopper
// has actually picked a real variant option, so we gate on this flag instead
// of trusting every theme:variant:change event.
function hasMadeVariantSelection(productSection) {
  return productSection.dataset.variantSelectionMade === "true";
}

// Capture phase runs top-down BEFORE the bubble-phase listener that dispatches
// theme:variant:change, so the flag is already set by the time that handler
// checks it — even for the very first real selection.
document.addEventListener(
  "change",
  function (e) {
    const input = e.target.closest(
      ".product__selectors [data-single-option-selector]"
    );
    if (!input || input.closest(".ProductForm__Variants")) return;

    const productSection = input.closest('[data-section-type="product"]');
    if (productSection) {
      productSection.dataset.variantSelectionMade = "true";
    }
  },
  true
);

// Global listener for variant changes (works on initial page AND after product swaps)
document.addEventListener("DOMContentLoaded", function () {
  const productSection = document.querySelector(
    '[data-section-type="product"]'
  );

  if (productSection) {
    // Listen for variant changes to add/remove Klaviyo button
    productSection.addEventListener("theme:variant:change", function (event) {
      if (event.detail && event.detail.variant) {
        const variant = event.detail.variant;

        if (hasMadeVariantSelection(productSection)) {
          updateColorSwatchAvailability(productSection, variant);
        }

        // Set up observer to watch for Klaviyo IFRAME (when variant is out of stock)

        const productJSON = productSection.querySelector("[data-product-json]");
        if (productJSON) {
          try {
            const productData = JSON.parse(productJSON.textContent);

            const observer = new MutationObserver(function () {
              // Find the Klaviyo iframe first
              const klaviyoIframe =
                document.getElementById("klaviyo-bis-iframe") ||
                document.querySelector('iframe[id*="klaviyo"]');

              if (klaviyoIframe) {
                setTimeout(() => {
                  try {
                    // Access the iframe's document
                    const iframeDoc =
                      klaviyoIframe.contentDocument ||
                      klaviyoIframe.contentWindow.document;

                    // Find the modal inside the iframe
                    const modalInIframe =
                      iframeDoc.getElementById("klaviyo-bis-modal");

                    if (modalInIframe) {
                      // Update product ID hidden input
                      const productInput =
                        modalInIframe.querySelector("#product");
                      if (productInput) {
                        productInput.value = productData.id;
                      }

                      // Find the variants select inside the modal
                      const modalVariantSelect =
                        modalInIframe.querySelector("#variants");

                      if (modalVariantSelect && productData.variants) {
                        // Update dropdown - ONLY out-of-stock variants
                        modalVariantSelect.innerHTML = "";

                        const outOfStockVariants = productData.variants.filter(
                          (v) => !v.available
                        );

                        outOfStockVariants.forEach((v) => {
                          const option = document.createElement("option");
                          option.value = v.id;
                          option.textContent =
                            v.title ||
                            `${v.option1 || ""} / ${v.option2 || ""}`.trim();
                          option.selected = v.id === variant.id;
                          modalVariantSelect.appendChild(option);
                        });
                      }
                    }
                  } catch (e) {
                    console.log("❌ Cannot access iframe:", e.message);
                  }
                }, 500);

                observer.disconnect();
              }
            });

            // Start observing for modal
            observer.observe(document.body, { childList: true, subtree: true });

            // Stop observing after 10 seconds
            setTimeout(() => observer.disconnect(), 10000);
          } catch (e) {
            console.error("❌ Error setting up observer:", e);
          }
        }

        // Update the hidden variants select on EVERY variant change
        const variantsSelect = document.getElementById("variants");

        if (variantsSelect) {
          variantsSelect.value = variant.id;
        }

        // Remove any existing Klaviyo buttons
        const existingKlaviyoBtn = productSection.querySelector(
          ".klaviyo-bis-trigger"
        );
        if (existingKlaviyoBtn) {
          existingKlaviyoBtn.remove();
        }

        // Only add button if variant is out of stock
        if (!variant.available) {
          setTimeout(() => {
            const productSubmitItemDiv = productSection.querySelector(
              ".product__submit__item"
            );

            if (productSubmitItemDiv) {
              // Don't show out of stock button if "Select Size" button is visible
              const selectSizeBtn = productSection.querySelector(
                ".product-size-unselected-btn"
              );
              if (selectSizeBtn && selectSizeBtn.style.display !== "none") {
                return;
              }

              // Check if button already exists - don't add duplicate
              const existingBtn = productSubmitItemDiv.querySelector(
                ".klaviyo-bis-trigger"
              );
              if (existingBtn) {
                return;
              }

              // Create simple button
              const klaviyoBtn = document.createElement("button");
              klaviyoBtn.type = "button";
              klaviyoBtn.className = "btn btn--primary btn--solid klaviyo-bis-trigger";
              klaviyoBtn.textContent = "OUT OF STOCK - NOTIFY ME WHEN AVAILABLE";

              // Add click handler to update modal first, then show it
              klaviyoBtn.addEventListener("click", function (e) {
                e.preventDefault();
                openBackInStockForm(productSection, variant.id);
              });

              // Add to page
              productSubmitItemDiv.appendChild(klaviyoBtn);
            }
          }, 300);
        }
      }
    });
  }
});

// Open Klaviyo's back-in-stock form for whichever product is currently on screen.
//
// Klaviyo's onsite script reads the product once, at load, and a swatch change is a
// pushState rather than a navigation — so the form it opens is still pointed at the product
// the visitor landed on. Hence the iframe surgery: open it, then rewrite the product id and
// the variant list from the JSON this page keeps current (see step 5 of
// updateProductFormData) before letting it be seen.
//
//   productSection    the [data-section-type="product"] wrapper to read the product from
//   selectedVariantId variant to pre-select, or null to leave the form on its first option
//                     (nothing is selected yet straight after a swatch change)
function bisFormIsHidden(iframe) {
  if (!iframe) return true;
  if (iframe.hidden || iframe.getAttribute("aria-hidden") === "true") return true;
  const display = iframe.style.display || window.getComputedStyle(iframe).display;
  return display === "none";
}

function hideBisForm(iframe) {
  if (!iframe) return;
  iframe.style.display = "none";
  iframe.setAttribute("aria-hidden", "true");
}

function getBisFocusable(root) {
  if (!root) return [];
  return Array.from(
    root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => {
    const style = el.ownerDocument.defaultView.getComputedStyle(el);
    return style.display !== "none" && style.visibility !== "hidden";
  });
}

function trapBisFocus(iframe, modal) {
  let iframeDoc = null;
  try {
    iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
  } catch (e) {
    iframeDoc = null;
  }
  const scope = modal || (iframeDoc && iframeDoc.body);

  const focusEdge = (last) => {
    const items = getBisFocusable(scope);
    if (items.length) {
      items[last ? items.length - 1 : 0].focus();
      return;
    }
    if (scope && typeof scope.focus === "function") {
      if (!scope.hasAttribute("tabindex")) scope.setAttribute("tabindex", "-1");
      scope.focus();
      return;
    }
    iframe.focus();
  };

  const onIframeKeydown = (e) => {
    if (e.key !== "Tab" || bisFormIsHidden(iframe)) return;
    const items = getBisFocusable(scope);
    if (!items.length) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = iframeDoc.activeElement;
    if (e.shiftKey && (active === first || !scope.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !scope.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };

  const onParentKeydown = (e) => {
    if (e.key !== "Tab" || bisFormIsHidden(iframe)) return;
    if (document.activeElement === iframe || !iframe.contains(document.activeElement)) {
      e.preventDefault();
      focusEdge(e.shiftKey);
    }
  };

  const onParentFocusIn = (e) => {
    if (bisFormIsHidden(iframe)) return;
    if (e.target === iframe || iframe.contains(e.target)) return;
    focusEdge(false);
  };

  document.addEventListener("keydown", onParentKeydown, true);
  document.addEventListener("focusin", onParentFocusIn, true);
  if (iframeDoc) iframeDoc.addEventListener("keydown", onIframeKeydown, true);

  return () => {
    document.removeEventListener("keydown", onParentKeydown, true);
    document.removeEventListener("focusin", onParentFocusIn, true);
    if (iframeDoc) iframeDoc.removeEventListener("keydown", onIframeKeydown, true);
  };
}

function restoreFocusOnBisClose(iframe, modal, trigger) {
  if (!iframe) return;

  const releaseTrap = trapBisFocus(iframe, modal);

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    observer.disconnect();
    document.removeEventListener("keydown", onKeydown, true);
    releaseTrap();
    if (trigger && typeof trigger.focus === "function" && document.body.contains(trigger)) {
      trigger.focus();
    }
  };

  const observer = new MutationObserver(() => {
    if (bisFormIsHidden(iframe)) finish();
  });
  observer.observe(iframe, {
    attributes: true,
    attributeFilter: ["style", "hidden", "aria-hidden", "class"],
  });

  const onKeydown = (e) => {
    if (e.key !== "Escape" || bisFormIsHidden(iframe)) return;
    e.preventDefault();
    hideBisForm(iframe);
    finish();
  };
  document.addEventListener("keydown", onKeydown, true);

  try {
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.addEventListener("click", (e) => {
      if (e.target.closest(".close, [data-dismiss], [aria-label*='lose' i]")) {
        hideBisForm(iframe);
        finish();
      }
    });
    iframeDoc.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || bisFormIsHidden(iframe)) return;
      e.preventDefault();
      hideBisForm(iframe);
      finish();
    });
  } catch (e) {
    // Cross-origin: parent Escape handler still closes the overlay.
  }
}

function revealBackInStockIframe(iframe, modal, trigger) {
  iframe.style.display = "block";
  iframe.removeAttribute("aria-hidden");

  const firstField =
    (modal &&
      modal.querySelector(
        'input:not([type="hidden"]), select, textarea, button, [href], [tabindex]:not([tabindex="-1"])'
      )) ||
    null;

  requestAnimationFrame(() => {
    if (firstField && typeof firstField.focus === "function") {
      firstField.focus();
    } else if (modal) {
      if (!modal.hasAttribute("tabindex")) modal.setAttribute("tabindex", "-1");
      modal.focus();
    } else {
      iframe.focus();
    }
  });

  restoreFocusOnBisClose(iframe, modal, trigger);
}

function openBackInStockForm(productSection, selectedVariantId, triggerEl) {
  if (!productSection) return;

  const klaviyoIframe = document.getElementById("klaviyo-bis-iframe");
  const trigger =
    triggerEl ||
    (document.activeElement && document.activeElement !== document.body
      ? document.activeElement
      : null);

  if (!klaviyoIframe) {
    console.log('⚠️ Klaviyo modal not found, refreshing page...');
    // Silently refresh the page to reinitialize Klaviyo
    window.location.replace(window.location.href);
    return;
  }

  // Keep iframe hidden while we update it
  klaviyoIframe.style.display = "none";

  // Trigger Klaviyo to load the modal (but keep it hidden)
  if (window._klOnsite) {
    window._klOnsite.push(["openForm", "backinstock"]);
  }

  // Wait for Klaviyo to populate, then update with correct data
  setTimeout(() => {
    try {
      const iframeDoc =
        klaviyoIframe.contentDocument || klaviyoIframe.contentWindow.document;
      const modalInIframe = iframeDoc.getElementById("klaviyo-bis-modal");

      if (modalInIframe) {
        const productJSON = productSection.querySelector("[data-product-json]");
        if (productJSON) {
          const productData = JSON.parse(productJSON.textContent);

          // Update product ID
          const productInput = modalInIframe.querySelector("#product");
          if (productInput) {
            productInput.value = productData.id;
          }

          // Update variants dropdown
          const modalVariantSelect = modalInIframe.querySelector("#variants");
          if (modalVariantSelect && productData.variants) {
            modalVariantSelect.innerHTML = "";

            const outOfStockVariants = productData.variants.filter(
              (v) => !v.available
            );

            outOfStockVariants.forEach((v) => {
              const option = document.createElement("option");
              option.value = v.id;
              option.textContent =
                v.title || `${v.option1 || ""} / ${v.option2 || ""}`.trim();
              if (selectedVariantId) {
                option.selected = v.id === selectedVariantId;
              }
              modalVariantSelect.appendChild(option);
            });
          }
        }
      }

      revealBackInStockIframe(klaviyoIframe, modalInIframe, trigger);
    } catch (e) {
      console.log("❌ Cannot update:", e.message);
      revealBackInStockIframe(klaviyoIframe, null, trigger);
    }
  }, 800);
}

// Reached from the sticky cart bar too, which has no button of its own to borrow: the
// trigger this file rebuilds only exists once a sold-out size has been picked.
window.MalbonBackInStock = { open: openBackInStockForm };

// Prefetch + pre-parse color variant products so clicks are instant
function prefetchNewColorVariants(productSection, sectionId) {
  const swatchButtons = productSection.querySelectorAll('.product__swatch--button');

  swatchButtons.forEach((button) => {
    const productUrl = button.dataset.swatchProductLink;
    if (!productUrl) return;

    const fullUrl = `${productUrl}?section_id=${sectionId}`;

    // Skip if already cached (parsed DOM is in domCache)
    if (domCache.has(fullUrl)) return;

    // Fetch at low priority, then parse + cache the DOM fragment
    fetch(fullUrl, { priority: "low", cache: "force-cache" })
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const section = doc.querySelector(".product__page--wrapper");
        if (section) {
          domCache.set(fullUrl, section);
        }
      })
      .catch(() => {
        // Silently fail — this is just prefetching
      });
  });
}
